import { createUseStyles } from '@/services/createUseStyles';
import { HEADER_HEIGHT, SIDEBAR_WIDTH } from '@/theme';
import { Box, Button, IconButton } from '@mui/material';
import { Theme } from '@mui/material/styles';
import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
const MIN_WIDTH = 200;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = SIDEBAR_WIDTH;

const useStyles = createUseStyles((theme: Theme) => ({
  DraggablePanel_root: {
    position: 'fixed',
    height: '100%',
    backgroundColor: theme.palette.background.paper,
    zIndex: theme.zIndex.drawer,
    willChange: 'width',
    transform: 'translateZ(0)', // Force GPU acceleration
    backfaceVisibility: 'hidden', // Reduce paint
    perspective: 1000, // Improve 3D rendering

    [theme.breakpoints.down('md')]: {
      width: '100% !important',
      maxWidth: '100% !important',
      boxShadow: theme.shadows[8],
      transform: 'translateX(-100%)',
      transition: 'transform 0.3s ease-in-out',
      '&.open': {
        transform: 'translateX(0)',
      },
    },
  },
  DraggablePanel_left: {
    left: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('md')]: {
      borderRight: 'none',
    },
  },
  DraggablePanel_right: {
    right: 0,
    borderLeft: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('md')]: {
      borderLeft: 'none',
    },
  },
  DraggablePanel_resizeHandle: {
    position: 'absolute',
    top: 0,
    width: 12,
    height: '100%',
    cursor: 'col-resize',
    backgroundColor: theme.palette.background.paper,
    transition: 'background-color 0.2s ease',
    border: 'none',
    background: 'none',
    padding: 0,
    margin: 0,
    outline: 'none',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, .5)',
    },
    '&:focus': {
      backgroundColor: 'rgba(25, 118, 210, .3)',
      outline: '2px solid',
      outlineColor: theme.palette.primary.main,
      outlineOffset: '-2px',
    },
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: 1,
      backgroundColor: theme.palette.divider,
      transition: 'background-color 0.2s ease',
    },
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  DraggablePanel_resizeHandle_left: {
    right: -8,
    '&::before': {
      right: 12,
    },
    '&::after': {
      right: 4,
    },
  },
  DraggablePanel_resizeHandle_right: {
    left: -8,
    '&::before': {
      left: 4,
    },
    '&::after': {
      left: 10,
    },
  },
  DraggablePanel_mobileToggleButton: {
    position: 'fixed',
    top: `calc(${HEADER_HEIGHT}px + 2px)`,
    left: theme.spacing(2),
    zIndex: theme.zIndex.drawer + 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '.25rem',
    maxWidth: 'calc(100% - 32px)',

    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  DraggablePanel_mobileToggleButton_icon: {
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
    flexShrink: 0,
  },
  DraggablePanel_mobileToggleButton_content: {
    flex: 1,
    minWidth: 0,
    maxWidth: 'calc(100% - 40px)',
  },
  DraggablePanel_mobileToggleButton_hidden: {
    display: 'none',
  },
  DraggablePanel_mobile_closeButtonSpacer: {
    height: 60,
  },
  DraggablePanel_mobileCloseButton: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'unset',
      position: 'sticky',
      borderRadius: 0,
      bottom: 63,
      left: 0,
      right: 0,
      width: '100%',
      height: 50,
      padding: theme.spacing(1),
    },
  },
}));

// Memoize the resize handle to prevent re-renders
const ResizeHandle = memo(
  ({ position, onMouseDown }: { position: 'left' | 'right'; onMouseDown: (event: React.MouseEvent) => void }) => {
    const c = useStyles();
    const handleClasses = [
      c.DraggablePanel_resizeHandle,
      position === 'left' ? c.DraggablePanel_resizeHandle_left : c.DraggablePanel_resizeHandle_right,
    ].join(' ');

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        // Convert keyboard event to mouse event for consistency
        const mouseEvent = {
          clientX: event.currentTarget.getBoundingClientRect().left + 6, // Center of handle
          preventDefault: () => {},
        } as React.MouseEvent;
        onMouseDown(mouseEvent);
      }
    };

    return (
      <button
        type='button'
        className={handleClasses}
        onMouseDown={onMouseDown}
        onKeyDown={handleKeyDown}
        aria-label={`Resize ${position} panel`}
        data-testid={`draggable-panel-resize-handle-${position}`}
      />
    );
  }
);

ResizeHandle.displayName = 'ResizeHandle';

export const DraggablePanel = ({
  children,
  position = 'left',
  className,
  style,
  startingWidth = DEFAULT_WIDTH,
  onWidthChange,
  mobileProperties,
}: {
  children: React.ReactNode;
  position?: 'left' | 'right';
  className?: string;
  style?: React.CSSProperties;
  onWidthChange?: (width: number) => void;
  startingWidth?: number;
  mobileProperties?: {
    toggleIcon?: React.ReactNode;
    toggleIconContent?: React.ReactNode;
    closeButtonLabel?: string;
  };
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [width, setWidth] = useState(startingWidth);
  const [isResizing, setIsResizing] = useState(false);
  const c = useStyles();

  // Refs for direct DOM manipulation
  const panelReference = useRef<HTMLDivElement>(null);
  const startXReference = useRef(0);
  const startWidthReference = useRef(0);
  const rafIdReference = useRef<number | undefined>(undefined);
  const lastWidthReference = useRef(startingWidth);
  const lastUpdateTimeReference = useRef(0);
  const isUpdatingReference = useRef(false);

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    setIsResizing(true);
    startXReference.current = event.clientX;
    startWidthReference.current = panelReference.current?.offsetWidth || DEFAULT_WIDTH;
    lastWidthReference.current = startWidthReference.current;
    event.preventDefault();
  }, []);

  const updateWidth = useCallback(
    (newWidth: number) => {
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        // Only update state if width changed significantly (by at least 5px)
        // and enough time has passed since last update (at least 50ms)
        const now = Date.now();
        if (Math.abs(newWidth - lastWidthReference.current) >= 5 && now - lastUpdateTimeReference.current >= 50) {
          lastWidthReference.current = newWidth;
          lastUpdateTimeReference.current = now;
          setWidth(newWidth);
          onWidthChange?.(newWidth);
        }
      }
    },
    [onWidthChange]
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isResizing || !panelReference.current) return;

      // Calculate new width based on mouse movement
      const deltaX = event.clientX - startXReference.current;
      const newWidth =
        position === 'left' ? startWidthReference.current + deltaX : startWidthReference.current - deltaX;

      // Apply width directly to DOM for immediate visual feedback
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        panelReference.current.style.width = `${newWidth}px`;
      }

      // Schedule state update for next frame, but only if not already updating
      if (!isUpdatingReference.current) {
        isUpdatingReference.current = true;

        if (rafIdReference.current) {
          cancelAnimationFrame(rafIdReference.current);
        }

        rafIdReference.current = requestAnimationFrame(() => {
          updateWidth(newWidth);
          isUpdatingReference.current = false;
        });
      }
    },
    [isResizing, position, updateWidth]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    if (rafIdReference.current) {
      cancelAnimationFrame(rafIdReference.current);
      rafIdReference.current = undefined;
    }

    // Ensure final width is applied to state
    if (panelReference.current) {
      const finalWidth = panelReference.current.offsetWidth;
      updateWidth(finalWidth);
    }
  }, [updateWidth]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (isResizing) {
      globalThis.addEventListener('mousemove', handleMouseMove);
      globalThis.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      globalThis.removeEventListener('mousemove', handleMouseMove);
      globalThis.removeEventListener('mouseup', handleMouseUp);
      if (rafIdReference.current) {
        cancelAnimationFrame(rafIdReference.current);
      }
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const panelClasses = [
    c.DraggablePanel_root,
    position === 'left' ? c.DraggablePanel_left : c.DraggablePanel_right,
    isOpen && 'open',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {mobileProperties?.toggleIcon && (
        <button
          type='button'
          className={classNames(c.DraggablePanel_mobileToggleButton, {
            [c.DraggablePanel_mobileToggleButton_hidden]: isOpen,
          })}
          onClick={handleToggle}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleToggle();
            }
          }}
          aria-label='Toggle panel'
          style={{
            border: 'none',
            background: 'none',
            padding: 0,
            margin: 0,
            cursor: 'pointer',
          }}
        >
          <IconButton className={c.DraggablePanel_mobileToggleButton_icon} size='small'>
            {mobileProperties?.toggleIcon}
          </IconButton>
          <div className={c.DraggablePanel_mobileToggleButton_content}>{mobileProperties?.toggleIconContent}</div>
        </button>
      )}
      <Box
        ref={panelReference}
        className={panelClasses}
        style={{
          width,
          minWidth: MIN_WIDTH,
          maxWidth: MAX_WIDTH,
          ...style,
        }}
      >
        {children}
        {mobileProperties?.closeButtonLabel && (
          <>
            <Box className={c.DraggablePanel_mobile_closeButtonSpacer} />
            <Button
              className={c.DraggablePanel_mobileCloseButton}
              variant='contained'
              color='primary'
              onClick={handleToggle}
            >
              {mobileProperties?.closeButtonLabel}
            </Button>
          </>
        )}
        <ResizeHandle position={position} onMouseDown={handleMouseDown} />
      </Box>
    </>
  );
};
